<template>
  <div class="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
    <div
      class="fixed -right-10 -top-2 z-50 h-0 w-0 border-b-[60px] border-l-[60px] border-r-[60px] border-b-black border-l-transparent border-r-transparent"
      style="transform: rotate(45deg)"
    >
      <span
        class="absolute left-1/2 top-[37px] -translate-x-1/2 text-xs font-bold text-white"
      >
        Illustrative
      </span>
    </div>
    <div
      class="overflow-hidden py-16"
      :style="bgStyle"
    >
      <div
        class="relative mx-auto flex max-w-4xl flex-col items-center justify-center px-4 text-center"
      >
        <img
          v-if="broker.url && !isLoading"
          class="broker-logo"
          :src="broker.url"
          alt="broker logo"
        />
        <div
          v-else
          class="mb-5 h-10 w-1/3 rounded bg-gray-300"
        ></div>
        <h1
          v-if="!isLoading"
          class="mb-4 text-4xl font-bold text-black md:text-5xl"
        >
          {{ broker.legal_name }}
        </h1>
        <div
          v-else
          class="mb-4 h-6 w-1/2 rounded bg-gray-300"
        ></div>
        <p class="text-gray-00 mx-auto max-w-xl">
          Find and book the best loads in real-time
        </p>
      </div>
    </div>

    <div class="mx-auto -mt-8 max-w-4xl px-4">
      <div
        class="mb-8 rounded-2xl border border-gray-200/50 bg-white/80 p-6 shadow-lg backdrop-blur-xl"
      >
        <div class="flex flex-col gap-4 md:flex-row">
          <div class="relative flex-1">
            <SearchIcon
              class="absolute left-4 top-4 text-gray-400"
              :size="18"
            />
            <input
              v-model="searchTerm"
              type="text"
              placeholder="Search by location or equipment type..."
              class="w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-gray-700 placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-gray-300"
            />
          </div>
          <button
            class="flex items-center justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-500"
          >
            <FilterIcon
              :size="18"
              class="mr-1"
            />
            Filters
          </button>
        </div>
      </div>
    </div>

    <div class="mx-auto max-w-6xl px-4 pb-12">
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="load in filteredLoads"
          :key="load.id"
          class="group cursor-pointer rounded-2xl border border-gray-200 bg-white/90 p-6 backdrop-blur-sm transition-all duration-300 hover:border-gray-300 hover:bg-white hover:shadow-xl"
        >
          <div class="mb-4 flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <div
                class="rounded-lg bg-white p-2 transition-colors group-hover:bg-gray-50"
              >
                <TruckIcon
                  class="text-gray-600"
                  :size="20"
                />
              </div>
              <span class="text-sm font-medium text-gray-600">
                {{ load.equipment }}
              </span>
            </div>
            <span class="text-lg font-bold text-gray-600">
              {{ load.rate }}
            </span>
          </div>

          <div class="space-y-3 text-sm text-gray-300">
            <div class="flex items-start space-x-2">
              <MapPinIcon
                class="mt-0.5 text-gray-400"
                :size="16"
              />
              <div class="text-left">
                <p class="font-medium text-gray-700">Origin</p>
                <p class="text-gray-400">
                  {{ load.origin }}
                </p>
              </div>
            </div>
            <div class="flex items-start space-x-2">
              <MapPinIcon
                class="mt-0.5 text-gray-400"
                :size="16"
              />
              <div>
                <p class="font-medium text-gray-700">Destination</p>
                <p class="text-gray-400">
                  {{ load.destination }}
                </p>
              </div>
            </div>

            <div
              class="mt-3 grid grid-cols-2 gap-2 border-t border-gray-100 pt-3"
            >
              <div class="flex items-center space-x-1">
                <WeightIcon
                  class="text-gray-400"
                  :size="14"
                />
                <span class="text-gray-700">{{ load.weight }}</span>
              </div>
              <div class="flex items-center space-x-1">
                <Package2Icon
                  class="text-gray-400"
                  :size="14"
                />
                <span class="text-gray-700">{{ load.pieces }} pcs</span>
              </div>
              <div class="flex items-center space-x-1">
                <ClockIcon
                  class="text-gray-400"
                  :size="14"
                />
                <span class="text-gray-700">{{ load.pickupTime }}</span>
              </div>
              <div class="flex items-center space-x-1">
                <CalendarIcon
                  class="text-gray-400"
                  :size="14"
                />
                <span class="text-gray-700">{{ load.date }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import {
  SearchIcon,
  FilterIcon,
  TruckIcon,
  MapPinIcon,
  CalendarIcon,
  Package2Icon,
  WeightIcon,
  ClockIcon,
} from 'lucide-vue-next';

const store = useStore();
const searchTerm = ref('');
const isLoading = ref(true);
const bgStyle = `background-image: linear-gradient(to left, #EBF4F5, #B5C6E0)`;

onMounted(async () => {
  const url = new URL(window.location.href);
  const storedId = localStorage.getItem('client_id');
  const clientId = storedId || url.searchParams.get('clientId');

  if (!clientId) {
    window.location.href = `${window.location.origin}/onboarding/`;
    return;
  }

  if (!storedId) {
    localStorage.setItem('client_id', clientId);
  }

  await store.dispatch('brokers/getLogoByClientId', clientId);

  if (url.search || url.hash) {
    window.history.replaceState({}, '', url.origin + url.pathname);
  }

  isLoading.value = false;
});

const broker = computed(() => {
  return store.state.brokers.logo;
});
const today = new Date();
const formatDate = (offset = 0) =>
  new Date(today.getTime() + offset * 86400000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const exampleLoads = [
  {
    id: 1,
    origin: 'Los Angeles, CA',
    destination: 'Phoenix, AZ',
    date: formatDate(0),
    rate: '$2,800',
    distance: '373 miles',
    equipment: 'Dry Van',
    weight: '42,000 lbs',
    pickupTime: '8:00 AM',
    pieces: '24',
  },
  {
    id: 2,
    origin: 'Seattle, WA',
    destination: 'Phoenix, AZ',
    date: formatDate(1),
    rate: '$1,200',
    distance: '174 miles',
    equipment: 'Flatbed',
    weight: '38,000 lbs',
    pickupTime: '10:30 AM',
    pieces: '12',
  },
  {
    id: 3,
    origin: 'Chicago, IL',
    destination: 'Detroit, MI',
    date: formatDate(2),
    rate: '$1,800',
    distance: '282 miles',
    equipment: 'Reefer',
    weight: '35,000 lbs',
    pickupTime: '9:15 AM',
    pieces: '18',
  },
  {
    id: 4,
    origin: 'Miami, FL',
    destination: 'Orlando, FL',
    date: formatDate(3),
    rate: '$950',
    distance: '236 miles',
    equipment: 'Box Truck',
    weight: '28,000 lbs',
    pickupTime: '7:45 AM',
    pieces: '15',
  },
  {
    id: 5,
    origin: 'Dallas, TX',
    destination: 'Houston, TX',
    date: formatDate(4),
    rate: '$1,100',
    distance: '239 miles',
    equipment: 'Dry Van',
    weight: '40,000 lbs',
    pickupTime: '11:00 AM',
    pieces: '30',
  },
  {
    id: 6,
    origin: 'Denver, CO',
    destination: 'Phoenix, AZ',
    date: formatDate(5),
    rate: '$2,200',
    distance: '520 miles',
    equipment: 'Flatbed',
    weight: '44,000 lbs',
    pickupTime: '6:30 AM',
    pieces: '22',
  },
];
const filteredLoads = computed(() => {
  if (!searchTerm.value) return exampleLoads;
  const term = searchTerm.value.toLowerCase();
  return exampleLoads.filter((load) => {
    return (
      load.origin.toLowerCase().includes(term) ||
      load.destination.toLowerCase().includes(term) ||
      load.equipment.toLowerCase().includes(term)
    );
  });
});
</script>

<style scoped>
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.broker-logo {
  margin: 0 auto;
  margin-bottom: 1.25rem;
  max-width: 60%;
  max-height: 60px;
}
</style>
